import React from 'react';
import { connect } from 'react-redux';
import Intercom from 'react-intercom';
import axios from 'axios';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { message, ConfigProvider, App as AntdApp } from 'antd';
import queryString from 'query-string';
import { LicenseManager } from 'ag-grid-enterprise';
import { withRouter } from '../containers/withRouter';
import { withTracker } from '../containers/withTracker';
import { isReceiver } from '../utils/UserUtils.ts';
import { withErrorBoundary } from './global/ErrorBoundary';
import {
  loadUserByToken,
  removeTokenAndUser,
  loadUserByOktaToken,
  refreshToken,
  getCognitoTokens,
} from '../actions/user';
import * as modalActions from '../actions/app/modal';
import { triggerUpdateCounter } from '../actions/catalogue/updateManager/update';
import { keys, exludedRequestPaths } from '../constants/ParamountReactConstants';
import { MODAL_TYPES } from '../constants/ModalConstants';
import MainPage from '../pages/MainPage';
import LoginPage from '../pages/LoginPage';
import RegisterPage from '../pages/RegisterPage.tsx';
import ResetPasswordPage from '../pages/ResetPasswordPage.tsx';
import AuthCallbackPage from '../pages/AuthCallbackPage.tsx';
import { notifyBugsnag } from '../actions/tools/BugsnagActions';
import { showUpgradePlanNotification } from '../actions/app/modal';
import antStyles from '../constants/AntStyles.json';

LicenseManager.setLicenseKey(keys.agTable.license_key);

const TOKEN_REFRESH_MARGIN = 5 * 60 * 1000; // 5 minutes before expiry
const TOKEN_CHECK_INTERVAL = 4 * 60 * 1000; // Check every 4 minutes

class App extends React.Component {
  constructor(props) {
    super(props);
    this.initInterceptors();
    this.tokenRefreshInterval = null;
  }

  async componentDidMount() {
    if (location.pathname === '/sso') {
      const values = queryString.parse(this.props.location.search);
      const { sso_token } = values;
      if (sso_token) await this.props.dispatch(loadUserByOktaToken(sso_token));
      this.props.navigate('/');
    } else {
      this.props.dispatch(loadUserByToken());
    }

    this.startTokenRefreshCheck();
  }

  componentWillUnmount() {
    this.stopTokenRefreshCheck();
  }

  startTokenRefreshCheck = () => {
    this.tokenRefreshInterval = setInterval(this.checkAndRefreshToken, TOKEN_CHECK_INTERVAL);
  };

  stopTokenRefreshCheck = () => {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }
  };

  checkAndRefreshToken = async () => {
    const { user } = this.props;
    if (!user?.id) return;

    const cognitoTokens = getCognitoTokens();
    if (!cognitoTokens?.expires_at) return;

    const timeUntilExpiry = cognitoTokens.expires_at - Date.now();

    if (timeUntilExpiry < TOKEN_REFRESH_MARGIN && timeUntilExpiry > 0) {
      try {
        await this.props.dispatch(refreshToken());
      } catch (error) {
        // Token refresh failed, user will be logged out on next 401 response
      }
    }
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.user.id && this.props.user !== nextProps.user) {
      axios.defaults.headers.common['Paramount-Access-Token'] = nextProps.user.token;
    }
  }

  componentDidUpdate() {
    const { user, brands, location, isReceiver } = this.props;

    if (location.pathname === '/') {
      if (brands.length > 0 && user.id)
        this.props.navigate({
          pathname: '/brand/home',
          search: location.search,
        });
      if (isReceiver)
        this.props.navigate({
          pathname: '/available-brands/all-brands',
          search: location.search,
        });
    }
  }

  checkUserOffline = () => {
    const { modalType } = this.props;
    if (!window.navigator.onLine && modalType !== MODAL_TYPES.USER_OFFLINE) {
      this.props.dispatch(modalActions.showModal(MODAL_TYPES.USER_OFFLINE));
    }
  };

  checkBackendDown = error => {
    const { modalType } = this.props;
    if (error.response && error.response.status === 502 && modalType !== MODAL_TYPES.BACKEND_DOWN) {
      this.props.dispatch(modalActions.showModal(MODAL_TYPES.BACKEND_DOWN));
    }
  };

  checkUnauthorizedUpdate = error => {
    const { t, isReceiver, brandId, user } = this.props;
    if (error.config) {
      const includedMethod = ['post', 'patch', 'put', 'delete'].includes(error.config.method);
      if (
        includedMethod &&
        error.config.url.includes('/items') &&
        !error.config.url.includes('/items/analyses') &&
        !error.config.url.includes('/items/search')
      ) {
        if (isReceiver) message.error(t('mainApp:changesNotSaved'));
        if (!user.brand_permissions.parent.edit.includes(brandId))
          message.error(t('mainApp:permissionError'));
        else this.props.dispatch(showUpgradePlanNotification());
      }
    }
  };

  initInterceptors = () => {
    axios.interceptors.response.use(
      response => {
        // eslint-disable-line
        this.checkRequestMethod(response.config.method, response.config.url);

        return response;
      },
      error => {
        this.checkUserOffline();
        this.checkBackendDown(error);
        if (this.props.isReceiver || (error.response && error.response.status === 404))
          this.checkUnauthorizedUpdate(error);

        // Do something with response error
        if (error.response && error.response.status === 401) {
          this.props.dispatch(removeTokenAndUser());
        } else if (axios.isCancel(error)) {
          // ignore canceled calls
          return new Promise(() => {});
        } else {
          this.props.dispatch(
            notifyBugsnag(
              new Error(
                `${error.config.method.toUpperCase()} request call failed with status ${
                  error.response && error.response.status
                }: ${error.request.responseURL}`
              ),
              {
                metadata: {
                  request: error.request,
                  response: error.response,
                },
              }
            )
          );
        }
        return Promise.reject(error);
      }
    );
  };

  checkRequestMethod = (method, path = '') => {
    // don't trigger updates in cypress, analyses might run a throw error, because other test deleted the item already
    if (window.Cypress) return;

    const includedMethod = ['post', 'patch', 'put', 'delete'].includes(method);
    const exludedPath = exludedRequestPaths.some(entry => {
      if (entry.exception) {
        return (
          entry.method === method && path.includes(entry.path) && !path.includes(entry.exception)
        );
      }
      return entry.method === method && path.includes(entry.path);
    });

    if (includedMethod && !exludedPath) this.props.dispatch(triggerUpdateCounter());
  };

  getRouteView = () => {
    const { location, user, fetchingUser } = this.props;

    if (location.pathname.startsWith('/register')) {
      return <RegisterPage />;
    }
    if (location.pathname === '/reset-password') {
      const params = queryString.parse(this.props.location.search) || {};
      const { token, email, code } = params;

      // Support both MySQL flow (token) and Cognito flow (email + code)
      if (token || (email && code)) {
        return <ResetPasswordPage token={token} email={email} code={code} />;
      }
    }
    if (location.pathname === '/login/driv') {
      return <LoginPage key={1} okta />;
    }
    if (location.pathname === '/auth/callback') {
      // If user is already logged in after SSO, redirect to home
      if (user.id) {
        this.props.navigate('/');
        return null;
      }
      if (!fetchingUser) {
        return <AuthCallbackPage />;
      }
      return null;
    }

    if (fetchingUser) {
      return null;
    }

    if (!user.id) {
      return <LoginPage key={1} />;
    }

    return <MainPage key={1} newVersion={this.props.newVersion} />;
  };

  getUserInfoForIntercom = () => {
    const { user, isReceiver } = this.props;
    if (user.id) {
      const name = `${user.first_name} ${user.last_name}`;
      const email = user.email;
      return {
        name,
        email,
        user_id: user.id,
        user_hash: user.intercom_user_hash,
        company: {
          id: user.parent_id || `r${user.receiver_id}`,
          name: user.account_name,
          company_account_type: isReceiver ? 'Receiver' : 'Brand',
        },
        account_type: isReceiver ? 'Receiver' : 'Brand',
      };
    }
    return {};
  };

  intercom = () => {
    const { mainMenuCollapsed } = this.props;
    const user = this.getUserInfoForIntercom();

    return (
      <Intercom
        key={2}
        appID={keys.intercom.app_id}
        {...user}
        alignment="left"
        horizontal_padding={mainMenuCollapsed ? 16 : 16}
        vertical_padding={mainMenuCollapsed ? 30 : 30}
      />
    );
  };

  view = () => {
    const stage = process.env.REACT_APP_STAGE;

    if (stage) {
      return [this.getRouteView(), this.intercom()];
    }
    return this.getRouteView();
  };

  render() {
    return (
      <ConfigProvider
        theme={{
          token: antStyles,
          hashed: false,
        }}
      >
        <AntdApp>{this.view()}</AntdApp>
      </ConfigProvider>
    );
  }
}

App.propTypes = {
  user: PropTypes.object,
  error: PropTypes.object,
};

App.defaultProps = {
  user: {},
  error: null,
};

function mapStateToProps(state) {
  return {
    user: state.user.user,
    fetchingUser: state.user.fetchingUser,
    brands: state.parent.brands.brands,
    brandId: state.parent.brands.selectedBrandId,
    newVersion: state.app.global.newVersion,
    mainMenuCollapsed: state.app.global.mainMenuCollapsed,
    modalType: state.app.modal.modalType,
    isReceiver: state.user.user && isReceiver(state.user.user),
  };
}

export { App };
export default withRouter(
  withTracker(connect(mapStateToProps)(withErrorBoundary(withTranslation()(App))))
);
